import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';

import { createRequiredContext } from 'src/context/createRequiredContext';

import type { ApplicationFilters, ApplicationTab, SearchApplicationsQuery } from './types';

import { parseApplicationsSearchParams, updateApplicationsSearchParams } from './search-params';

type ApplicationsContextValue = {
  tab: ApplicationTab;
  setTab: (tab: ApplicationTab) => void;
  search: string;
  setSearch: (value: string) => void;
  filters: ApplicationFilters;
  activeFilterCount: number;
  setFilterValue: (key: keyof ApplicationFilters, value: string[] | undefined) => void;
  clearAllFilters: () => void;
  query: SearchApplicationsQuery;
  page: number;
  setPage: (page: number) => void;
};

const [useApplications, ApplicationsProvider] = createRequiredContext<ApplicationsContextValue>();

const FILTER_DEFAULTS: ApplicationFilters = {
  setupStatus: undefined,
  tasks: undefined,
  productIncluded: undefined,
  assuranceReached: undefined,
};

const ApplicationsContextProvider = ({ children }: React.PropsWithChildren) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { tab, search, page, filters } = useMemo(() => parseApplicationsSearchParams(searchParams), [searchParams]);

  const activeFilterCount = useMemo(
    () => Object.values(filters).filter((value) => Array.isArray(value) && value.length > 0).length,
    [filters],
  );

  const handleSetTab = useCallback(
    (nextTab: ApplicationTab) => {
      setSearchParams((prev) => updateApplicationsSearchParams(prev, { tab: nextTab }));
    },
    [setSearchParams],
  );

  const handleSetSearch = useCallback(
    (value: string) => {
      setSearchParams((prev) => updateApplicationsSearchParams(prev, { search: value }), { replace: true });
    },
    [setSearchParams],
  );

  const setFilterValue = useCallback(
    (key: keyof ApplicationFilters, value: string[] | undefined) => {
      setSearchParams((prev) => updateApplicationsSearchParams(prev, { filters: { [key]: value } }), {
        replace: true,
      });
    },
    [setSearchParams],
  );

  const clearAllFilters = useCallback(() => {
    setSearchParams((prev) => updateApplicationsSearchParams(prev, { filters: FILTER_DEFAULTS }), { replace: true });
  }, [setSearchParams]);

  const handleSetPage = useCallback(
    (nextPage: number) => setSearchParams((prev) => updateApplicationsSearchParams(prev, { page: nextPage })),
    [setSearchParams],
  );

  const query = useMemo<SearchApplicationsQuery>(() => ({ tab, search, filters }), [tab, search, filters]);

  const value: ApplicationsContextValue = {
    tab,
    setTab: handleSetTab,
    search,
    setSearch: handleSetSearch,
    filters,
    activeFilterCount,
    setFilterValue,
    clearAllFilters,
    query,
    page,
    setPage: handleSetPage,
  };

  return <ApplicationsProvider value={value}>{children}</ApplicationsProvider>;
};

export { useApplications, ApplicationsContextProvider };
